import React from 'react';
import { Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  RocketLaunchIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/solid';
import { BuildingStorefrontIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import PublicNavbar from './common/PublicNavbar';
import PublicFooter from './common/PublicFooter';
import { useRegister } from './hooks/useRegister';

const Register: React.FC = () => {
  const {
    formData, formStatus, showPassword, setShowPassword,
    handleChange, usernameError, usernameValid, handleSubmit, benefits,
    agreedToTerms, setAgreedToTerms,
  } = useRegister();

  /* Success State */
  if (formStatus.success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicNavbar solid />
        <div
          style={{
            flex: 1,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #3b82f6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: '100px',
            padding: '100px 1rem 3rem',
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '24px',
              maxWidth: '520px',
              width: '100%',
              padding: 'clamp(32px, 6vw, 48px)',
              textAlign: 'center',
              boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
            }}
          >
            <div
              style={{
                width: '72px', height: '72px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.25rem',
              }}
            >
              <EnvelopeIcon style={{ width: '40px', height: '40px', color: '#1e3a8a' }} />
            </div>
            <h3 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.5rem' }}>Check your email</h3>
            <p style={{ color: '#64748b', marginBottom: '1rem', lineHeight: 1.6 }}>
              We've sent a verification link to<br />
              <strong style={{ color: '#0f172a' }}>{formData.email}</strong>
            </p>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', lineHeight: 1.6, fontSize: '0.9rem' }}>
              Click the link in the email to activate your account. The link expires in 24 hours.
              Don't forget to check your spam folder.
            </p>
            <div
              style={{
                background: '#f0f9ff', borderRadius: '12px', padding: '14px 20px',
                marginBottom: '1.75rem', border: '1px solid #bae6fd',
              }}
            >
              <span style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: '2px' }}>Your branded portal (available after verification)</span>
              <strong style={{ color: '#1e3a8a', fontSize: '1.05rem' }}>{formData.username}.sourdoughsy.com</strong>
            </div>
            <Link
              to="/customer-login"
              className="btn d-inline-flex align-items-center gap-2"
              style={{
                borderRadius: '12px',
                padding: '12px 32px',
                fontWeight: 700,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
                color: 'white',
                border: 'none',
                boxShadow: '0 4px 15px rgba(30,58,138,0.3)',
                textDecoration: 'none',
              }}
            >
              Go to Login
            </Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  /* Registration Form */
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicNavbar solid />

      <div style={{ flex: 1, paddingTop: '72px' }}>
        <Row className="g-0 m-0" style={{ minHeight: 'calc(100vh - 72px)' }}>

          {/* Left panel - gradient hero (desktop only) */}
          <Col lg={5} className="d-none d-lg-flex"
            style={{
              background: 'linear-gradient(160deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: 'clamp(40px, 5vw, 80px)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '260px', height: '260px', borderRadius: '50%', background: 'rgba(59,130,246,0.12)' }} />
            <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(59,130,246,0.08)' }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div className="d-flex align-items-center gap-2 mb-4">
                <BuildingStorefrontIcon style={{ width: '28px', height: '28px', color: '#60a5fa' }} />
                <span style={{ fontSize: '1.15rem', fontWeight: 700, color: 'white' }}>Sourdoughsy</span>
              </div>

              <h2 style={{ color: 'white', fontWeight: 800, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', lineHeight: 1.3, marginBottom: '0.75rem' }}>
                Everything you need to run your wholesale bakery
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', lineHeight: 1.6, marginBottom: '2.5rem' }}>
                Join bakeries that trust Sourdoughsy to manage their customers, orders, and finances.
              </p>

              <div className="d-flex flex-column gap-3">
                {benefits.map((b) => (
                  <div key={b.title} className="d-flex align-items-start gap-3">
                    <div
                      style={{
                        width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                        background: 'rgba(255,255,255,0.08)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <b.icon style={{ width: '20px', height: '20px', color: '#60a5fa' }} />
                    </div>
                    <div>
                      <div style={{ color: 'white', fontWeight: 700, fontSize: '0.92rem', marginBottom: '1px' }}>{b.title}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.83rem', lineHeight: 1.45 }}>{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          {/* Right - Form */}
          <Col lg={7} xs={12}
            style={{
              background: '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'clamp(24px, 4vw, 48px)',
            }}
          >
            <div style={{ maxWidth: '520px', width: '100%' }}>
              {/* Mobile-only branding */}
              <div className="d-lg-none text-center mb-4">
                <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                  <BuildingStorefrontIcon style={{ width: '24px', height: '24px', color: '#1e3a8a' }} />
                  <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e3a8a' }}>Sourdoughsy</span>
                </div>
              </div>

              <h2 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '4px', fontSize: 'clamp(1.4rem, 3vw, 1.7rem)' }}>
                Create your account
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '1.75rem' }}>
                Free to start — no credit card required
              </p>

              {formStatus.error && (
                <Alert variant="danger" className="py-2 px-3" style={{ fontSize: '0.88rem', borderRadius: '10px' }}>
                  {formStatus.error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label style={lblStyle}>Company / Bakery Name *</Form.Label>
                  <Form.Control
                    type="text" name="companyName" placeholder="e.g. Sunrise Sourdough Co."
                    value={formData.companyName} onChange={handleChange} required style={inputStyle}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={lblStyle}>Contact Person *</Form.Label>
                  <Form.Control
                    type="text" name="contactPerson" placeholder="Full name"
                    value={formData.contactPerson} onChange={handleChange} required style={inputStyle}
                  />
                </Form.Group>

                <Row>
                  <Col xs={12} sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={lblStyle}>Email *</Form.Label>
                      <Form.Control
                        type="email" name="email" placeholder="you@company.com"
                        value={formData.email} onChange={handleChange} required style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={lblStyle}>Phone</Form.Label>
                      <Form.Control
                        type="tel" name="phone" placeholder="(555) 123-4567"
                        value={formData.phone} onChange={handleChange} style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} sm={5}>
                    <Form.Group className="mb-3">
                      <Form.Label style={lblStyle}>City</Form.Label>
                      <Form.Control
                        type="text" name="city" placeholder="Your city"
                        value={formData.city} onChange={handleChange} style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} sm={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={lblStyle}>State</Form.Label>
                      <Form.Control
                        type="text" name="state" placeholder="TX"
                        value={formData.state} onChange={handleChange} style={inputStyle}
                        maxLength={2}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} sm={3}>
                    <Form.Group className="mb-3">
                      <Form.Label style={lblStyle}>Zip Code</Form.Label>
                      <Form.Control
                        type="text" name="postalCode" placeholder="78701"
                        value={formData.postalCode} onChange={handleChange} style={inputStyle}
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div style={{ height: '1px', background: '#e2e8f0', margin: '8px 0 20px' }} />

                <Form.Group className="mb-3">
                  <Form.Label style={lblStyle}>
                    Username *
                    <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.78rem', marginLeft: '6px' }}>
                      (this becomes your subdomain)
                    </span>
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text" name="username" placeholder="yourbakery"
                      value={formData.username} onChange={handleChange} required
                      autoCapitalize="none" autoCorrect="off"
                      minLength={10} maxLength={40}
                      isInvalid={!!usernameError}
                      isValid={usernameValid}
                      style={{ ...inputStyle, borderRadius: '10px 0 0 10px', borderRight: 'none' }}
                    />
                    <span style={{
                      padding: '11px 14px', background: '#f1f5f9',
                      border: '1.5px solid #e2e8f0', borderLeft: 'none',
                      borderRadius: '0 10px 10px 0', color: '#64748b',
                      fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'nowrap',
                    }}>
                      .sourdoughsy.com
                    </span>
                  </div>
                  {usernameError ? (
                    <div style={{ color: '#dc3545', fontSize: '0.75rem', marginTop: '4px' }}>
                      {usernameError}
                    </div>
                  ) : (
                    <Form.Text style={{ color: usernameValid ? '#198754' : '#94a3b8', fontSize: '0.75rem' }}>
                      {usernameValid ? '✓ Looks good!' : '10–40 characters. Lowercase letters, numbers, and hyphens only.'}
                    </Form.Text>
                  )}
                </Form.Group>

                <Row>
                  <Col xs={12} sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={lblStyle}>Password *</Form.Label>
                      <div style={{ position: 'relative' }}>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'} name="password" placeholder="Min 6 characters"
                          value={formData.password} onChange={handleChange} required style={{ ...inputStyle, paddingRight: '44px' }}
                        />
                        <button type="button" tabIndex={-1} onClick={() => setShowPassword(!showPassword)}
                          style={{ position: 'absolute', right: '1px', top: '1px', bottom: '1px', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '0 9px 9px 0' }}>
                          {showPassword ? <EyeSlashIcon style={{ width: '16px', height: '16px', color: '#64748b' }} /> : <EyeIcon style={{ width: '16px', height: '16px', color: '#64748b' }} />}
                        </button>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={lblStyle}>Confirm Password *</Form.Label>
                      <div style={{ position: 'relative' }}>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'} name="confirmPassword" placeholder="Repeat password"
                          value={formData.confirmPassword} onChange={handleChange} required style={{ ...inputStyle, paddingRight: '44px' }}
                        />
                        <button type="button" tabIndex={-1} onClick={() => setShowPassword(!showPassword)}
                          style={{ position: 'absolute', right: '1px', top: '1px', bottom: '1px', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '0 9px 9px 0' }}>
                          {showPassword ? <EyeSlashIcon style={{ width: '16px', height: '16px', color: '#64748b' }} /> : <EyeIcon style={{ width: '16px', height: '16px', color: '#64748b' }} />}
                        </button>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="terms-checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    label={
                      <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                        I agree to the{' '}
                        <a href="/page/terms" target="_blank" rel="noopener noreferrer"
                          style={{ color: '#1e3a8a', fontWeight: 600, textDecoration: 'underline' }}>
                          Terms &amp; Conditions
                        </a>
                      </span>
                    }
                  />
                </Form.Group>

                <Button
                  type="submit" disabled={formStatus.loading || !agreedToTerms}
                  className="w-100 d-flex align-items-center justify-content-center gap-2 mt-1"
                  style={{
                    borderRadius: '12px', padding: '14px', fontWeight: 700,
                    fontSize: '1rem', background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
                    color: 'white', border: 'none', boxShadow: '0 4px 15px rgba(30,58,138,0.25)',
                    minHeight: '50px',
                  }}
                >
                  <RocketLaunchIcon style={{ width: '20px', height: '20px' }} />
                  {formStatus.loading ? 'Creating Account...' : 'Create Free Account'}
                </Button>
              </Form>

              <p className="text-center mt-3" style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: 0 }}>
                Already have an account?{' '}
                <Link to="/customer-login" style={{ color: '#1e3a8a', fontWeight: 600, textDecoration: 'none' }}>
                  Log in
                </Link>
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <PublicFooter />
    </div>
  );
};

const lblStyle: React.CSSProperties = { fontWeight: 600, fontSize: '0.88rem', color: '#374151' };
const inputStyle: React.CSSProperties = {
  padding: '11px 14px', borderRadius: '10px',
  border: '1.5px solid #e2e8f0', fontSize: '0.92rem',
  transition: 'border-color 0.2s',
};

export default Register;
